import { createSelector } from "reselect";
import { formatDate } from "utils/formatDate";
import { DATE_FORMATS } from "../../constants";
import { RootState } from "../../reducers";
import { BACKUP_FILE_NOTES } from "./constants";
import { ExtensionState, extensionState } from "./extensions";
import {
  DATABASES,
  DATABASE_REPLICA_STATUSES,
  DATABASE_STATUSES,
  DBServiceParams,
  ExtensionInfo,
  ExtensionSetting,
  DatabaseCustomOptions,
  Database,
  BackupSet,
  DatabaseBackupStatus,
  DatabaseEncodings,
  DatabaseMetrics,
  DatabaseResetPassword,
  DbaasQuotas,
  TableCronStatus,
  TableDatabase,
  TableBackupSet,
  TableDatabaseBackupFiles,
  TableDatabaseChild,
  TableDatabaseCustomOptions,
  TableDatabaseUser,
  TableReplicaDatabase
} from "./types";

export const DATABASES_LABELS = {
  [DATABASES.MYSQL]: "MySQL",
  [DATABASES.MARIADB]: "MariaDB",
  [DATABASES.POSTGRESQL]: "PostgreSQL",
  [DATABASES.MONGODB]: "MongoDB",
  [DATABASES.TIMESCALEDB]: "TimescaleDB",
  [DATABASES.REDIS]: "Redis"
};

export const DATABASES_DEFAULT_PORTS = {
  [DATABASES.MYSQL]: 3306,
  [DATABASES.MARIADB]: 3306,
  [DATABASES.POSTGRESQL]: 5432,
  [DATABASES.MONGODB]: 27017,
  // TimescaleDB speaks the PostgreSQL protocol on the PostgreSQL port.
  [DATABASES.TIMESCALEDB]: 5432,
  [DATABASES.REDIS]: 6379
};

export const supportsNamedDatabases = (serviceName?: string): boolean =>
  serviceName !== DATABASES.REDIS;

export const supportsEncodings = (serviceName?: string): boolean =>
  [
    DATABASES.MYSQL,
    DATABASES.MARIADB,
    DATABASES.POSTGRESQL,
    DATABASES.TIMESCALEDB
  ].some((engine) => engine === serviceName);

export const restoreNeedsDatabase = (serviceName?: string): boolean =>
  serviceName !== DATABASES.REDIS;

export const supportsPerDatabaseBackups = (serviceName?: string): boolean =>
  serviceName !== DATABASES.REDIS;

export const supportsReplicas = (serviceName?: string): boolean =>
  serviceName !== DATABASES.MONGODB;

export const supportsPerDatabaseGrants = (serviceName?: string): boolean =>
  serviceName !== DATABASES.REDIS;

export const supportsCustomParameters = (
  serviceName?: string,
  version?: string,
  imageFlavor?: string
): boolean =>
  supportsEncodings(serviceName) ||
  serviceName === DATABASES.REDIS ||
  (serviceName === DATABASES.MONGODB &&
    version?.split(".")[0] === "6" &&
    imageFlavor !== "bitnami");

// A replica's databases, extensions and settings follow its primary; the API refuses edits.
export const databasesEditable = (mode?: DATABASE_REPLICA_STATUSES): boolean =>
  mode !== DATABASE_REPLICA_STATUSES.REPLICA;

// Missing means an API that predates tenant-managed accounts: editable.
export const accountsEditable = (
  database?: Pick<Database, "accounts_editable"> | null
): boolean => database?.accounts_editable !== false;

export const accountsReadOnlyReason = (
  database?: Pick<
    Database,
    "accounts_editable" | "accounts_read_only_reason"
  > | null
): string =>
  accountsEditable(database)
    ? ""
    : database?.accounts_read_only_reason ||
      "Databases and users are read-only for this instance.";

// The catalogue is the capability: a service lists extensions only if it has any.
export const supportsExtensions = (catalogue?: ExtensionInfo[]): boolean =>
  Boolean(catalogue?.length);

// PgBouncer pools PostgreSQL-family connections (the operator's poolerUnsupported):
// not on a replica, and not on the Bitnami image.
export const supportsPooler = (
  database?: Pick<Database, "service_name" | "mode" | "image_flavor"> | null
): boolean =>
  (database?.service_name === DATABASES.POSTGRESQL ||
    database?.service_name === DATABASES.TIMESCALEDB) &&
  database.mode !== DATABASE_REPLICA_STATUSES.REPLICA &&
  database.image_flavor !== "bitnami";

// The pooler is no longer offered; one already enabled stays switchable off.
export const poolerEditable = (
  database?: Pick<
    Database,
    "service_name" | "mode" | "image_flavor" | "pooler"
  > | null
): boolean => supportsPooler(database) && Boolean(database?.pooler?.enabled);

export const SERVER_WIDE_GRANT_SCOPE = "0";

export const isServing = (status?: DATABASE_STATUSES): boolean =>
  status === DATABASE_STATUSES.ACTIVE || status === DATABASE_STATUSES.DEGRADED;

const newestFirst = (versions: string[]) =>
  [...versions].sort((a, b) =>
    b.localeCompare(a, undefined, { numeric: true })
  );

// The API offers only current versions for create, in catalogue order; the first is
// what an empty pick gets.
export const createVersionOptions = (
  versions?: string[]
): { label: string; value: string }[] | undefined =>
  versions &&
  newestFirst(versions).map((version) => ({
    label: version === versions[0] ? `${version} (default)` : version,
    value: version
  }));

// The API returns the versions the instance may move to; the running one is
// included so a pending upgrade can be cancelled by picking it.
export const versionSelectOptions = (
  database?: Pick<
    Database,
    "version" | "observed_version" | "available_versions"
  > | null
): { label: string; value: string }[] => {
  const runningVersion = database?.observed_version || database?.version;
  if (!database?.available_versions?.length || !runningVersion) {
    return [];
  }
  return newestFirst(database.available_versions).map((version) => ({
    label:
      version === runningVersion
        ? `${version} (current)`
        : version === database.version
          ? `${version} (requested)`
          : version,
    value: version
  }));
};

export const haMemberCount = (serviceName?: string): number =>
  serviceName === DATABASES.MONGODB ? 3 : 2;

const toTableDatabase = (database: Database): TableDatabase => ({
  ...database,
  connectionString: database.connection_string,
  versionString: `${String(DATABASES_LABELS[database.service_name])} ${
    database.version
  }`,
  cpuString: `${database.cpu / 1000}`,
  memoryString: `${String(database.memory / 1024)} GiB`,
  diskString: `${String(database.disk)} MiB`,
  volumeCapacity: `${String(database.volume_capacity_used)}`,
  serviceName: database.service_name,
  ageString: database.creation_time
    ? formatDate(new Date(database.creation_time * 1000), DATE_FORMATS.DATETIME)
    : "",
  ipACL: database.ip_acl || []
});

export const tableDatabasesSelector = (
  state: RootState
): TableDatabase[] | null =>
  state.databases.databases?.map(toTableDatabase) || null;

const rawDatabaseSelector = (state: RootState) => state.databases.database;

export const databaseSelector = createSelector(
  [rawDatabaseSelector],
  (database): TableDatabase | null =>
    database ? toTableDatabase(database) : null
);

export const DBServiceParamsSelector = (
  state: RootState
): DBServiceParams | null => state.databases.DBServiceParams;

export const DBServicesParamsSelector = (
  state: RootState
): DBServiceParams[] | null => state.databases.DBServicesParams;

const NO_EXTENSIONS: ExtensionInfo[] = [];

export const extensionCatalogueSelector = createSelector(
  [DBServiceParamsSelector],
  (params): ExtensionInfo[] => params?.extensions ?? NO_EXTENSIONS
);

export const extensionCataloguesSelector = createSelector(
  [DBServicesParamsSelector],
  (paramsList): Record<string, ExtensionInfo[]> =>
    Object.fromEntries(
      (paramsList || []).map((params) => [
        params.service_name,
        params.extensions ?? NO_EXTENSIONS
      ])
    )
);

export const tableReplicaDatabasesSelector = (
  state: RootState
): TableReplicaDatabase[] | null =>
  state.databases.replicaDatabases?.map(toTableDatabase) || null;

export const backupStatusSelector = (
  state: RootState
): DatabaseBackupStatus | null => state.databases.databaseBackupStatus;

export const backupFilesSelector = (
  state: RootState
): TableDatabaseBackupFiles[] | null =>
  state.databases.databaseBackupFiles?.map((file) => ({
    id: file.etag,
    etag: file.etag,
    file_name: file.file_name,
    file_size: file.file_size,
    database: file.database,
    taken: file.taken,
    safety_copy: file.safety_copy,
    unattributed: file.unattributed,
    fileSize: `${String((file.file_size / 1024 / 1024).toFixed(2))} MiB`,
    note: file.unattributed
      ? BACKUP_FILE_NOTES.UNATTRIBUTED
      : file.database && !file.taken
        ? BACKUP_FILE_NOTES.OLDER_LAYOUT
        : ""
  })) || null;

// A table row cannot carry the object names a restore needs.
export const rawBackupSetsSelector = (state: RootState): BackupSet[] | null =>
  state.databases.databaseBackupSets;

export const tableBackupSetsSelector = (
  state: RootState
): TableBackupSet[] | null =>
  state.databases.databaseBackupSets?.map((set) => ({
    id: set.id,
    taken: set.taken
      ? formatDate(new Date(set.taken), DATE_FORMATS.DATETIME)
      : set.id,
    databases: set.databases?.length
      ? set.databases.join(", ")
      : "whole instance",
    fileCount: set.files?.length || 0,
    totalSize: `${String((set.total_size / 1024 / 1024).toFixed(2))} MiB`,
    safety: set.safety_copy ? "Pre-restore copy" : "Backup"
  })) || null;

export const cronStatusSelector = (
  state: RootState
): TableCronStatus[] | null =>
  state.databases.databaseBackupStatus?.cron_runs?.map((cron) => ({
    id: cron.id,
    date: cron.date,
    status: cron.status,
    dateString: formatDate(new Date(cron.date * 1000), DATE_FORMATS.DATETIME)
  })) || null;

export const areDatabaseBackupFilesLoadingSelector = (
  state: RootState
): boolean => state.databases.areDatabaseBackupFilesLoading;

export const isDatabaseLoadingSelector = (state: RootState): boolean =>
  state.databases.isDatabaseLoading;

export const areDatabasesLoadingSelector = (state: RootState): boolean =>
  state.databases.areDatabasesLoading;

export const isDatabaseGettingManagementUrlSelector = (
  state: RootState
): boolean => state.databases.isDatabaseGettingManagementUrl;

export const databaseResetPasswordSelector = (
  state: RootState
): DatabaseResetPassword | null =>
  state.databases.databaseResetPassword || null;

export const areReplicaDatabasesLoadingSelector = (state: RootState): boolean =>
  state.databases.areReplicaDatabasesLoading;

export const dbaasQuotasSelector = (state: RootState): DbaasQuotas | null =>
  state.databases.dbaasQuotas || null;

export const areDbaasQuotasLoadingSelector = (state: RootState): boolean =>
  state.databases.areDbaasQuotasLoading;

export const isDatabaseCreatingSelector = (state: RootState): boolean =>
  state.databases.isDatabaseCreating;

export const isDatabaseUpdatingSelector = (state: RootState): boolean =>
  state.databases.isDatabaseUpdating;

export const isDatabaseDeletingSelector = (state: RootState): boolean =>
  state.databases.isDatabaseDeleting;

export const isDatabaseRestoringSelector = (state: RootState): boolean =>
  state.databases.isDatabaseRestoring;

export const isDatabaseRollingBackSelector = (state: RootState): boolean =>
  state.databases.isDatabaseRollingBack;

export const isDatabaseBackingUpSelector = (state: RootState): boolean =>
  state.databases.isDatabaseBackingUp;

export const isDatabaseS3LinkingSelector = (state: RootState): boolean =>
  state.databases.isDatabaseS3Linking;

export const isDatabaseS3UnlinkingSelector = (state: RootState): boolean =>
  state.databases.isDatabaseS3Unlinking;

export const isCronBackupUpdatingSelector = (state: RootState): boolean =>
  state.databases.isCronBackupUpdating;

export const isCronBackupDeletingSelector = (state: RootState): boolean =>
  state.databases.isCronBackupDeleting;

export const areDBServiceParamsLoadingSelector = (state: RootState): boolean =>
  state.databases.areDBServiceParamsLoading;

export const areAllServicesParamsLoadingSelector = (
  state: RootState
): boolean => state.databases.areDBServicesParamsLoading;

export const isDatabaseStatusChangingSelector = (state: RootState): boolean =>
  state.databases.isDatabaseStatusChanging;

export const isDatabasePasswordResettingSelector = (
  state: RootState
): boolean => state.databases.isDatabasePasswordResetting;

export const isDatabaseCustomOptionsLoadingSelector = (
  state: RootState
): boolean => state.databases.isDatabaseCustomOptionsLoading;

export const databaseCustomOptionsSelector = (
  state: RootState
): TableDatabaseCustomOptions[] | null =>
  state.databases.databaseCustomOptions?.map((customOption) => ({
    ...customOption,
    id: customOption.name,
    nameString: customOption.title
      ? `${customOption.title} (${customOption.name})`
      : customOption.name,
    unit: customOption.unit || "",
    min_value: customOption.min_value || "",
    max_value: customOption.max_value || "",
    default_value: customOption.default_value || "",
    custom_value: customOption.custom_value || "",
    effective_value: customOption.effective_value || "",
    state: customOption.state ?? "unknown"
  })) || null;

export const databaseEncodingsSelector = (
  state: RootState
): DatabaseEncodings | null => state.databases.databaseEncodings;

export const defaultEncoding = (
  options: string[] = [],
  preferred?: string
): string | undefined =>
  preferred && options.includes(preferred) ? preferred : options[0];

export const isDatabaseCustomOptionsUpdatingSelector = (
  state: RootState
): boolean => state.databases.isDatabaseCustomOptionsUpdating;

const rawDatabaseChildrenSelector = (state: RootState) =>
  state.databases.databaseChildren;

const ENCODING_REJECTED_SUFFIX = "; created with the server default";

const markedItem = (text: string, state: ExtensionState) =>
  state === "applied"
    ? text
    : `${text} (${state === "pending" ? "pending" : "not applied"})`;

export const tableDatabaseChildrenSelector = createSelector(
  [rawDatabaseChildrenSelector],
  (databaseChildren): TableDatabaseChild[] | null =>
    databaseChildren?.map((db) => {
      const extensions = (db.extensions || []).map((name) => ({
        name,
        ...extensionState(db, name)
      }));
      const settings = Object.entries(db.settings || {}).map(([key, value]) => {
        const reason = db.rejected_settings?.[key];
        const state: ExtensionState =
          reason !== undefined
            ? "rejected"
            : db.pending_settings?.includes(key)
              ? "pending"
              : "applied";
        return { name: key, text: `${key}=${value}`, reason, state };
      });
      return {
        id: db.name,
        name: db.name,
        charset: db.charset,
        collation: db.collation,
        extensions: db.extensions || [],
        extensionsString: extensions
          .map(({ name, state }) => markedItem(name, state))
          .join(", "),
        rejected_extensions: db.rejected_extensions || {},
        pending_extensions: db.pending_extensions || {},
        settings: db.settings || {},
        settingsString: settings
          .map(({ text, state }) => markedItem(text, state))
          .join(", "),
        rejected_settings: db.rejected_settings || {},
        pending_settings: db.pending_settings || [],
        rejected_encoding: db.rejected_encoding || "",
        notes: [
          db.rejected_encoding &&
            `charset and collation: ${db.rejected_encoding}${ENCODING_REJECTED_SUFFIX}`,
          ...extensions.map(
            ({ name, reason }) => reason && `${name}: ${reason}`
          ),
          ...settings.map(({ name, reason }) => reason && `${name}: ${reason}`)
        ]
          .filter(Boolean)
          .join("; ")
      };
    }) || null
);

export const tableDatabaseUsersSelector = (
  state: RootState
): TableDatabaseUser[] | null =>
  state.databases.databaseUsers?.map((user) => ({
    id: user.name,
    name: user.name,
    permissions: user.permissions
      ? user.permissions.map((p) => `${p.database}:${p.permission}`).join(", ")
      : ""
  })) || null;

export const isDatabaseChildrenLoadingSelector = (state: RootState): boolean =>
  state.databases.isDatabaseChildrenLoading;

export const isDatabaseUsersLoadingSelector = (state: RootState): boolean =>
  state.databases.isDatabaseUsersLoading;

export const databaseUserSelector = (state: RootState): Database | null =>
  state.databases.databaseUser;

export const isDatabaseEncodingsLoadingSelector = (state: RootState): boolean =>
  state.databases.isDatabaseEncodingsLoading;

export const createdDatabasePasswordSelector = (
  state: RootState
): string | null => state.databases.createdDatabasePassword;

export const databaseMetricsSelector = (
  state: RootState
): DatabaseMetrics | null => state.databases.databaseMetrics;

export const isDatabaseMetricsLoadingSelector = (state: RootState): boolean =>
  state.databases.isDatabaseMetricsLoading;

const s3BucketsListSelector = (state: RootState) =>
  state.objectStorage.bucketsList;

export const selectOptionBackupBucketsSelector = createSelector(
  [s3BucketsListSelector],
  (bucketsList): { label: string; value: string }[] =>
    Array.isArray(bucketsList)
      ? bucketsList.map(({ name }) => ({ label: name, value: name }))
      : []
);

export const customParameterSetting = (
  option: DatabaseCustomOptions
): ExtensionSetting => ({
  name: option.name,
  title: option.title,
  type: option.type as ExtensionSetting["type"],
  min: option.min_value,
  max: option.max_value,
  default: option.default_value,
  unit: option.unit,
  values: option.values?.split(",").filter(Boolean),
  description: option.description,
  allowEmpty: option.allow_empty === "true",
  specialValues: option.special_values?.split(",").filter(Boolean)
});

export const backupStoragePickerSelector = createSelector(
  [(state: RootState) => state.databases.backupStoragePicker],
  (picker) => ({
    credentials: picker.details,
    credentialsContext: picker.context,
    credentialsList: picker.credentials,
    credentialsListContext: picker.context,
    credentialsListError: picker.errors.keys,
    credentialsError: picker.errors.details,
    buckets: picker.buckets,
    bucketsContext: picker.context
      ? { ...picker.context, credsId: picker.credentialId }
      : null,
    bucketsError: picker.errors.buckets,
    credentialsLoading: picker.loadingDetails,
    credentialsListLoading: picker.loadingKeys,
    bucketsLoading: picker.loadingBuckets,
    credentialsCreating: picker.creatingKey,
    bucketCreating: picker.creatingBucket,
    createdCredentials: picker.createdCredentials
      ? { params: picker.context, credentials: picker.createdCredentials }
      : null,
    createdBucket:
      picker.createdBucketName && picker.context
        ? {
            params: {
              ...picker.context,
              credsId: picker.credentialId,
              data: { name: picker.createdBucketName }
            }
          }
        : null,
    createCredentialsError: picker.errors.createKey,
    createBucketError: picker.errors.createBucket
  })
);

export const backupStorageLinkResultSelector = createSelector(
  [(state: RootState) => state.databases],
  (state) => ({
    loading: state.isDatabaseS3Linking,
    successful: state.s3LinkSuccessful,
    error: state.s3LinkError
  })
);

export const backupScheduleResultSelector = createSelector(
  [(state: RootState) => state.databases],
  (state) => ({
    error: state.backupScheduleError,
    saved: state.backupScheduleSaved
  })
);

export const backupFilesErrorSelector = (state: RootState) =>
  state.databases.backupFilesError;
